import React, { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { useModalProps } from 'app/components/Settings/useModalProps';
import { InputField } from 'app/components/Core';
import { FolderItem } from 'types/song';
import { useSongFolderRenameForm } from 'app/views/SongFolder/useSongFolderRenameForm';

export interface SongFolderRenameDialogComponentProps {
  open: boolean;
  folder: FolderItem;
  onClose: () => void;
  onSubmit: (values: { title: string }) => Promise<void>;
}

export const SongFolderRenameDialogComponent: React.FC<SongFolderRenameDialogComponentProps> = ({
  open,
  folder,
  onClose,
  onSubmit,
}) => {
  const form = useSongFolderRenameForm({ folder });

  const {
    handleSubmit,
    register,
    reset,
    formState: { isSubmitting, isValid, isDirty, errors },
  } = useSongFolderRenameForm({ folder });

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  const { modalProps, contentProps } = useModalProps(open);

  const titleFieldProps = register('title', {
    setValueAs: (value) => value.trim(),
  });

  return (
    <Modal isOpen={open} onClose={onClose} {...modalProps}>
      <ModalOverlay />
      <ModalContent {...contentProps}>
        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            data-testid="rename-song-folder-form"
          >
            <ModalHeader data-testid="dialog-title" title={folder.title}>
              {folder.title}
            </ModalHeader>
            <ModalBody overscrollBehavior="none">
              <InputField
                {...titleFieldProps}
                label="New Song Folder Title"
                isInvalid={Boolean(errors.title)}
                errorMessage={errors.title?.message}
                data-testid="SongFolderTitle"
              />
            </ModalBody>
            <ModalFooter>
              <Button onClick={onClose} variant="outlined">
                Cancel
              </Button>
              <Button
                isDisabled={!isDirty || isSubmitting || !isValid}
                type="submit"
                data-testid="SongFolderRenameDialog-change-btn"
                ml={2}
              >
                Rename
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};
